// @ts-strict-ignore
import * as React from 'react';

import { useDocumentTags } from '@/components/UserTags';
import { NEEDL_BOOKMARK_TAG } from '@/components/UserTags/common';
import { useMutateDocumentTag } from '@/components/UserTags/hooks/useMutateDocumentTag';
import { useMutateTags } from '@/components/UserTags/hooks/useMutateTags';
import { useConstructSearchQueryParams } from '@/hooks/useConstructSearchQueryParams';

const BookmarkContext = React.createContext<BookmarkContextType | null>(null);

export const useBookmarkContext = () => {
  const context = React.useContext(BookmarkContext);

  if (!context) {
    throw new Error(
      'useBookmarkContext must be used within a BookmarkProvider'
    );
  }
  return context;
};

export const BookmarkProvider = ({
  children,
  deeplink,
  onSuccess,
  disabledOverride,
}: BookmarkProviderType) => {
  const [disabled, setDisabled] = React.useState(false);

  const { data: documentTags, isLoading: documentTagsLoading } =
    useDocumentTags(deeplink);

  const isBookmarked = !!documentTags?.tags?.includes(NEEDL_BOOKMARK_TAG);

  const isLoading = documentTagsLoading;

  const { board_id, channel_id } = useConstructSearchQueryParams();

  const mutate = useMutateDocumentTag(deeplink);
  const createBookmarkMutation = useMutateTags();

  const handleToggle = React.useCallback(async () => {
    if (!documentTags) {
      return;
    }

    setDisabled(true);

    const tags = isBookmarked
      ? documentTags.tags.filter((tag) => tag !== NEEDL_BOOKMARK_TAG)
      : [...documentTags.tags, NEEDL_BOOKMARK_TAG];

    // TODO: @meghanacosmos let's revist this once you're back
    if (!isBookmarked) {
      await createBookmarkMutation.mutateAsync({ tag: NEEDL_BOOKMARK_TAG });
    }

    mutate.mutate(
      { tags, channelId: channel_id, feedId: board_id, isBookmarked },
      {
        onSettled: () => {
          setDisabled(false);
        },
        onSuccess: () => {
          onSuccess?.();
        },
      }
    );
  }, [
    board_id,
    channel_id,
    createBookmarkMutation,
    documentTags,
    isBookmarked,
    mutate,
    onSuccess,
  ]);

  return (
    <BookmarkContext.Provider
      value={React.useMemo(
        () => ({
          disabled: disabled || disabledOverride,
          isLoading,
          isBookmarked,
          handleToggle,
        }),
        [disabled, disabledOverride, handleToggle, isBookmarked, isLoading]
      )}
    >
      {children}
    </BookmarkContext.Provider>
  );
};

type BookmarkProviderType = {
  children: React.ReactNode;
  deeplink: string;
  onSuccess?: () => void;
  disabledOverride?: boolean;
};

type BookmarkContextType = {
  disabled: boolean;
  isLoading: boolean;
  isBookmarked: boolean;
  handleToggle: () => void;
};
